import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  Rocket,
  Target,
  Users,
  TrendingUp,
  Globe,
  Zap,
  CheckCircle,
  Award,
} from "lucide-react";
import { portfolioData } from "../mock";

const FOCUS_AREAS = [
  {
    icon: <TrendingUp className="w-8 h-8" />,
    title: "DeFi & Payments",
    description:
      "Decentralized finance solutions, payment infrastructure, and cross-border remittance platforms",
  },
  {
    icon: <Globe className="w-8 h-8" />,
    title: "Web3 Infrastructure",
    description:
      "Blockchain protocols, NFT platforms, and decentralized applications built for African markets",
  },
  {
    icon: <Zap className="w-8 h-8" />,
    title: "Enterprise Blockchain",
    description:
      "Supply chain solutions, identity verification, and enterprise-grade blockchain implementations",
  },
];

const INVESTMENT_CRITERIA = [
  "Seed to Series A stage companies",
  "Africa-focused or serving African markets",
  "Strong technical founding team",
  "Clear product-market fit or path to PMF",
  "Scalable business model",
  "Blockchain/crypto-native solutions",
];

export const Home = () => {
  // Show only featured companies on home page (first 2)
  const [projects] = useState(portfolioData.slice(0, 2));
  const loading = false;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Investing in Africa's{" "}
              <span className="text-orange-600">Blockchain Future</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              A crypto venture capital firm partnering with exceptional founders
              building transformative blockchain ventures across Africa. From
              seed to scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Submit Your Pitch <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-300 text-slate-900 hover:bg-slate-100"
                >
                  View Portfolio
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center p-8 rounded-xl bg-slate-50 border border-slate-200">
              <TrendingUp className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-4xl font-bold text-slate-900 mb-2">$50M+</h3>
              <p className="text-slate-600">Assets Under Management</p>
            </div>
            <div className="text-center p-8 rounded-xl bg-slate-50 border border-slate-200">
              <Rocket className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-4xl font-bold text-slate-900 mb-2">
                {portfolioData.length}+
              </h3>
              <p className="text-slate-600">Portfolio Companies</p>
            </div>
            <div className="text-center p-8 rounded-xl bg-slate-50 border border-slate-200">
              <Target className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-4xl font-bold text-slate-900 mb-2">8</h3>
              <p className="text-slate-600">African Countries</p>
            </div>
            <div className="text-center p-8 rounded-xl bg-slate-50 border border-slate-200">
              <Users className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-4xl font-bold text-slate-900 mb-2">3</h3>
              <p className="text-slate-600">Successful Exits</p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Investment Thesis
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We invest in early-stage blockchain and cryptocurrency ventures
              solving real problems across African markets
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {FOCUS_AREAS.map((area, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-6">
                  {area.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {area.title}
                </h3>
                <p className="text-slate-600">{area.description}</p>
              </div>
            ))}
          </div>

          {/* Investment Stage & Size */}
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">
                  Investment Stage
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Target className="w-6 h-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">
                        Seed Stage
                      </h4>
                      <p className="text-slate-600">
                        $500K - $2M initial investment
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Target className="w-6 h-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Series A</h4>
                      <p className="text-slate-600">
                        $2M - $5M follow-on investment
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">
                  What We Look For
                </h3>
                <ul className="space-y-3">
                  {INVESTMENT_CRITERIA.map((criteria, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{criteria}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Our Portfolio
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Backing exceptional founders building the future of blockchain in
              Africa
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading projects...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {projects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/portfolio/${project.slug}`}
                    className="group"
                  >
                    <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl transition-all duration-300">
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={project.image}
                          alt={project.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-4 right-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              project.status === "Live"
                                ? "bg-green-100 text-green-700"
                                : project.status === "Development"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {project.status}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-sm text-orange-600 font-semibold mb-2">
                          {project.category}
                        </p>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">
                          {project.name}
                        </h3>
                        <p className="text-slate-600 mb-4">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link to="/portfolio">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-300 text-slate-900 hover:bg-slate-100"
                  >
                    View All Projects <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Value Proposition for Founders */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                More Than Capital
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We provide hands-on support to help our portfolio companies
                succeed in Africa's dynamic blockchain ecosystem.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mr-4 flex-shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      Strategic Guidance
                    </h3>
                    <p className="text-slate-600">
                      Deep operational experience in blockchain, fintech, and
                      African markets
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mr-4 flex-shrink-0">
                    <Globe className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      Network Access
                    </h3>
                    <p className="text-slate-600">
                      Introductions to customers, partners, and follow-on
                      investors across Africa
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mr-4 flex-shrink-0">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      Regulatory Navigation
                    </h3>
                    <p className="text-slate-600">
                      Guidance on compliance and regulatory frameworks across
                      African jurisdictions
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-2xl p-10 text-white">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Raise Capital?
              </h3>
              <p className="text-orange-100 mb-6">
                We're actively looking for exceptional founding teams building
                transformative blockchain ventures in Africa.
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>Review your pitch deck within 48 hours</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>Decision within 2-3 weeks</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>Founder-friendly terms</span>
                </div>
              </div>

              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-orange-600 hover:bg-slate-100 w-full"
                >
                  Submit Your Pitch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Partner With Us to Scale Your Venture
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Let's discuss how we can help accelerate your Web3 venture across
            Africa
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-orange-600 hover:bg-slate-100"
            >
              Get in Touch <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
